/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/require-default-props */
import { faChevronLeft, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import {
  QUESTION_VALUE_TYPE,
  QUESTION_FILTER_TYPE,
  ONBOARDING_STEPS,
  COLOR_SCHEME_ONBOARDING_STEPS,
  OPEN_TEXT_ONBOARDING_STEPS,
  QUESTION_TYPE,
} from '../../../../constants/common';
import { buildUpdatedDimensions, resolveOnboardingSteps } from './dimension.utils';
import { SurveyThemeNulled, surveyWizardThemes } from '../../../../constants/themes';
import FilterCategory from './FilterCategory';
import DataBinning from './DataBinning';
import './styles.css';
import AnswerList from './AnswerList';
import LabelList from './LabelList';

import benTooltip from '../../../../Components/BenTooltip';
import useAutoSave from '../../hooks/useAutoSave';
import useOnboarding from '../../hooks/useOnboarding';

// The S-type question editor. All form state is hook state; persisting goes
// through the onSave prop (useQuestionEditor.saveQuestion) and preview updates
// go through mirrorToStore, the single bridge to the shared Chart component.
const QuestionEditForm = ({
  sample,
  dimensions,
  visualizations,
  surveyLocationFilter,
  busy,
  busyLabel,
  onClose,
  onSave,
  onSaved,
  mirrorToStore,
}) => {
  const { isStepVisible, completeStep } = useOnboarding();
  const [selectedTheme, setSelectedTheme] = useState();
  const [prompt, setPrompt] = useState('');
  const [labels, setLabels] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [binningConfiguration, setBinningConfiguration] = useState();
  const [filterType, setFilterType] = useState('');
  const [steps, setSteps] = useState(ONBOARDING_STEPS);
  const [hiddenAnswers, setHiddenAnswers] = useState([]);
  const [answerIdOrders, setAnswerIdOrders] = useState([]);
  const [labelIdOrders, setLabelIdOrders] = useState([]);

  const onQuestionPromtChange = (e) => {
    setPrompt(e.target.value);
  };

  const onAnswerChange = (e, index) => {
    const answersToUpdate = [...answers];
    answersToUpdate[index].value = e.target.value;
    setAnswers([...answersToUpdate]);
  };

  const onHiddeChange = (hiddenList) => {
    setHiddenAnswers(hiddenList);
  };
  const onAnswerIdOrderChange = (idOrders) => {
    setAnswerIdOrders([...idOrders]);
  };
  const onLabelIdOrderChange = (labelOrder) => {
    setLabelIdOrders([...labelOrder]);
  };

  const onLabelChange = (e, index) => {
    const labelsToUpdate = [...labels];
    labelsToUpdate[index].value = e.target.value;
    setLabels([...labelsToUpdate]);
  };

  // Initialize the form from the loaded sample + dimensions. Deliberately NOT
  // keyed on filterType: this effect writes filterType, so re-running it on
  // every filter change would snap the radio back to the stored value and make
  // the Filter Category selection impossible to change.
  useEffect(() => {
    if (!sample || !dimensions) return;
    const text = [QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(
      sample?.Data.valueType
    )
      ? sample.Data.text
      : sample.Data.label || sample.Data.text;

    setPrompt(text || '');
    setFilterType(sample.Data.filterType || '');

    if (sample?.Data.valueType !== QUESTION_VALUE_TYPE.MULTIPLE) {
      const allowedValues = dimensions[0]?.Data?.allowedValues || {};
      const answerList = Object.keys(allowedValues)
        .filter((key) => key >= 0)
        .map((key) => ({ id: key, value: allowedValues[key] }));
      setAnswers(answerList);

      setHiddenAnswers(sample.Data.hiddenAnswers || []);
      const answerIds = answerList.map((answer) => answer.id);
      const idOrders =
        sample.Data.answerOrders && sample.Data.answerOrders.length > 0
          ? sample.Data.answerOrders
          : answerIds;
      setAnswerIdOrders(idOrders);
    } else {
      setAnswers([]);
      setHiddenAnswers([]);
      setAnswerIdOrders([]);
    }

    if (QUESTION_VALUE_TYPE.OPEN_NUMERIC === sample?.Data.valueType) {
      setBinningConfiguration({
        ...dimensions[0]?.Data?.binningConfiguration,
        label: dimensions[0]?.Data?.description,
      });
    } else {
      setBinningConfiguration(undefined);
    }

    if ([QUESTION_VALUE_TYPE.MULTIPLE, QUESTION_VALUE_TYPE.GRID].includes(sample?.Data.valueType)) {
      const labelList = dimensions
        .filter((dimension) => dimension.Data.valueType !== QUESTION_VALUE_TYPE.OPEN_TEXT)
        .map((dimension) => ({
          id: dimension.Id,
          value: dimension.Data.label,
        }));
      const currentOrderId = labelList.map((label) => label.id);
      const labelOrders =
        sample.Data.labelOrders && sample.Data.labelOrders.length > 0
          ? sample.Data.labelOrders
          : currentOrderId;
      setLabels(labelList);
      setLabelIdOrders(labelOrders);
    } else {
      setLabels([]);
      setLabelIdOrders([]);
    }
  }, [sample, dimensions]);

  // Which onboarding step set applies depends on the question type AND the
  // current filter choice, so it follows filterType as the user changes it.
  useEffect(() => {
    if (!sample || !dimensions) {
      setSteps(null);
      return;
    }
    setSteps(resolveOnboardingSteps(sample?.Data.valueType, filterType));
  }, [sample, dimensions, filterType]);

  // Deep clone and fold edited answers, labels, prompt and binning back into dimensions.
  const getUpdatedDimensions = useCallback(
    () =>
      buildUpdatedDimensions({
        dimensions,
        answers,
        labels,
        prompt,
        valueType: sample?.Data.valueType,
        binningConfiguration,
      }),
    [dimensions, answers, labels, prompt, sample, binningConfiguration]
  );

  // Push the current form state into the store so the Chart preview follows
  // filter-type changes immediately (the store is what Chart renders from).
  const mirrorCurrent = useCallback(
    (theme = null, nextFilterType = null) => {
      const updatedSample = {
        ...sample,
        Data: {
          ...sample.Data,
          label: prompt,
          text: prompt,
          filterType: nextFilterType || filterType,
        },
      };
      mirrorToStore(updatedSample, getUpdatedDimensions(), theme);
    },
    [sample, prompt, filterType, mirrorToStore, getUpdatedDimensions]
  );

  const handleSave = useCallback(async () => {
    if (!sample) return;
    const samplePatch = {
      Id: sample.Id,
      Data: {
        label: prompt,
        text: prompt,
        filterType,
        hiddenAnswers,
        answerOrders: answerIdOrders,
        labelOrders: labelIdOrders,
        questionType: QUESTION_TYPE[selectedTheme?.index],
      },
    };
    await onSave({
      samplePatch,
      updatedDimensions: getUpdatedDimensions(),
      surveyLocationFilter,
      surveyId: sample.Parent,
    });
    onSaved();
  }, [
    sample,
    prompt,
    filterType,
    hiddenAnswers,
    answerIdOrders,
    labelIdOrders,
    selectedTheme,
    getUpdatedDimensions,
    surveyLocationFilter,
    onSave,
    onSaved,
  ]);

  const autoSave = useAutoSave(handleSave);
  const { markDirty } = autoSave;

  const onBinningConfiguration = (value) => {
    markDirty();
    setBinningConfiguration(value);
  };

  const onChangeTheme = (theme) => {
    markDirty();
    setSelectedTheme(theme);
  };

  const onFilterTypeChange = (e) => {
    markDirty();
    setFilterType(e.target.value);
    if (e.target.value === QUESTION_FILTER_TYPE.ATTRIBUTE) {
      mirrorCurrent(SurveyThemeNulled, e.target.value);
    } else {
      mirrorCurrent(selectedTheme, e.target.value);
    }
  };

  useEffect(() => {
    if (!sample || !visualizations || visualizations.length <= 0) return;
    const themeIndex = QUESTION_TYPE.indexOf(sample?.Data.questionType);
    const currentThemes =
      surveyWizardThemes[themeIndex] || surveyWizardThemes[visualizations.details.scheme.index];
    setSelectedTheme(currentThemes);
  }, [sample, visualizations]);

  const showStep3 = steps ? isStepVisible(steps.step3) : false;
  const showStep5 = steps ? isStepVisible(steps.step5) : false;
  const showStep6 = steps ? isStepVisible(steps.step6) : false;

  return (
    <Col md="3" className="p-3 survey-sidebar edit-mode position-relative">
      {busy && (
        <div className="editor-loading-overlay">
          <Spinner animation="border" role="status" />
          {busyLabel}
        </div>
      )}
      <span className="d-flex" onClick={onClose} onKeyDown={onClose} role="button" tabIndex={0}>
        <FontAwesomeIcon size="sm" className="mr-3" icon={faChevronLeft} />
        Back to overview
      </span>
      <Col className="info-edit-form edit-body">
        {steps && (
          <OverlayTrigger
            placement="right"
            defaultShow={showStep3}
            show={showStep3}
            overlay={(props) =>
              benTooltip({
                ...props,
                title: steps.step3.title,
                content: steps.step3.content,
                close: () => completeStep(steps.step3.value),
              })
            }
          >
            <div>
              <h4>Question Text</h4>
              <Form.Control
                as="textarea"
                className="mt-3"
                value={prompt}
                onChange={onQuestionPromtChange}
                onBlur={markDirty}
                style={{ height: '100px' }}
                maxLength={500}
                placeholder="Survey Description"
              />
            </div>
          </OverlayTrigger>
        )}
        {QUESTION_VALUE_TYPE.OPEN_NUMERIC === sample?.Data.valueType && (
          <DataBinning
            binningConfiguration={binningConfiguration}
            onBinningConfigurationChange={(e) => onBinningConfiguration(e)}
          />
        )}

        {/* Answers */}
        <AnswerList
          sample={sample}
          answers={answers}
          hiddenAnswers={hiddenAnswers}
          answerIdOrders={answerIdOrders}
          onAnswerIdOrderChange={onAnswerIdOrderChange}
          onAnswerChange={onAnswerChange}
          onHiddeChange={onHiddeChange}
          onDirty={markDirty}
        />

        <LabelList
          sample={sample}
          labels={labels}
          labelIdOrders={labelIdOrders}
          onLabelIdOrderChange={onLabelIdOrderChange}
          onLabelChange={onLabelChange}
          onDirty={markDirty}
        />
        {sample &&
          ![
            QUESTION_VALUE_TYPE.GRID_MULTIPLE,
            QUESTION_VALUE_TYPE.RANK_ORDER,
            QUESTION_VALUE_TYPE.OPEN_TEXT,
          ].includes(sample?.Data.valueType) && (
            <FilterCategory
              filterType={filterType}
              onFilterTypeChange={onFilterTypeChange}
              sample={sample}
              steps={steps}
              surveyLocationFilter={surveyLocationFilter}
            />
          )}
        {sample &&
          filterType !== QUESTION_FILTER_TYPE.ATTRIBUTE &&
          [QUESTION_VALUE_TYPE.SINGLE, QUESTION_VALUE_TYPE.GRID].includes(
            sample?.Data.valueType
          ) && (
            <>
              <h4 className="mb-2">
                Select Color Scheme
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Gradient: For data in an ordered series (like increasing lengths of time)
                      <br />
                      Discrete: For data with no inherent order or rank (like school subjects)
                      <br />
                      Positive: For ordered data with two ends and a middle (like Likert scale
                      <br />
                      questions)
                    </Tooltip>
                  }
                  delay={100}
                >
                  <FontAwesomeIcon className="cursor-pointer ml-2" size="1x" icon={faInfoCircle} />
                </OverlayTrigger>
              </h4>
              {steps && (
                <OverlayTrigger
                  placement="right"
                  defaultShow={showStep5}
                  show={showStep5}
                  overlay={(props) =>
                    benTooltip({
                      ...props,
                      title: steps?.step5?.title,
                      content: steps?.step5?.content,
                      close: () => completeStep(steps.step5.value),
                    })
                  }
                >
                  <div>
                    {surveyWizardThemes.map((theme) => (
                      <div className="mb-2" key={`theme-${theme.index}`}>
                        <div
                          className={`colors-rounded ${
                            theme.index === selectedTheme?.index ? 'selected' : ''
                          }`}
                          onClick={() => onChangeTheme(theme)}
                          role="button"
                          style={{ display: 'flex' }}
                        >
                          {theme.scheme.map((color) => (
                            <div
                              role="button"
                              key={`color-${color}`}
                              style={{ backgroundColor: color, flex: 1, height: 20 }}
                            />
                          ))}
                        </div>
                        <span>{theme.label === 'Blue' ? 'Positive/Negative' : theme.label}</span>
                      </div>
                    ))}
                  </div>
                </OverlayTrigger>
              )}
            </>
          )}
      </Col>
      {steps && (
        <OverlayTrigger
          placement="right"
          defaultShow={showStep6}
          show={showStep6}
          overlay={(props) =>
            benTooltip({
              ...props,
              title: steps.step6.title,
              content: steps.step6.content,
              close: () => completeStep(steps.step6.value),
            })
          }
        >
          <Button
            variant="primary"
            onClick={autoSave.saveNow}
            disabled={busy}
            className="btn btn-custom dark mt-3"
          >
            Save
          </Button>
        </OverlayTrigger>
      )}
    </Col>
  );
};

QuestionEditForm.propTypes = {
  sample: PropTypes.instanceOf(Object),
  dimensions: PropTypes.arrayOf(PropTypes.object),
  visualizations: PropTypes.instanceOf(Object),
  surveyLocationFilter: PropTypes.instanceOf(Object),
  busy: PropTypes.bool,
  busyLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  mirrorToStore: PropTypes.func.isRequired,
};

export default QuestionEditForm;
